import * as React from "react";
import {
  Box,
  Typography,
  Button,
  TextField,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Chip,
  Snackbar,
  Alert,
  CircularProgress,
  FormControl,
  Select,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import PillSearchField from "../../components/PillSearchField";
import PersonIcon from "@mui/icons-material/Person";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import {
  sendUserDataToBackend,
  fetchUsersFromBackend,
  deleteUserFromBackend,
} from "../../api/sendUserDataToBackend";

import { useState } from "react";

function Operators({ embedded = false }) {
  // Data state
  const [operators, setOperators] = React.useState([]);
  const [filteredOperators, setFilteredOperators] = useState([]);
  const [searchQuery, setSearchQuery] = React.useState("");

  // Loading and error states
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [operationLoading, setOperationLoading] = React.useState(false);

  // Pagination state
  const [paginationModel, setPaginationModel] = React.useState({
    page: 0,
    pageSize: 30,
  });
  const previousPageRef = React.useRef(0); // Track previous page to detect page changes

// ...existing code...

React.useEffect(() => {
  let isMounted = true;

  const fetchUsersIncrementally = async () => {
    try {
      setLoading(true);
      setError(null);

      let page = 1;
      const pageSize = 120;
      let allFetchedUsers = [];

      while (true) {
        console.log(`📡 Fetching users page ${page}...`);
        const fetchedUsers = await fetchUsersFromBackend(page, pageSize);

        if (!fetchedUsers || fetchedUsers.length === 0) {
          console.log("✅ No more users to fetch.");
          break;
        }

        allFetchedUsers = [...allFetchedUsers, ...fetchedUsers];

        if (fetchedUsers.length < pageSize) {
          break;
        }

        page += 1;
      }

      if (isMounted) {
        setOperators(allFetchedUsers);
      }
    } catch (error) {
      console.error("Failed to fetch users:", error);
      if (isMounted) {
        setError(error.message || "Failed to load operators");
        setSnackbar({
          open: true,
          message: "Failed to load operators",
          severity: "error",
        });
      }
    } finally {
      if (isMounted) setLoading(false);
    }
  };

  fetchUsersIncrementally();

  return () => {
    isMounted = false;
  };
}, []);

  // UI state
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [selectedOperator, setSelectedOperator] = React.useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [addUserDialogOpen, setAddUserDialogOpen] = React.useState(false);

  // Form data for add user
  const [formData, setFormData] = React.useState({
    name: "",
    email: "",
    password: "",
    role: "Operator", // Default role
  });

  const [snackbar, setSnackbar] = React.useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Helper function to refresh operators list
// Helper function to refresh operators list incrementally
const refreshOperators = async () => {
  try {
    setError(null);
    setLoading(true);

    let page = 1;
    const pageSize = 120;
    let allFetchedUsers = [];

    while (true) {
      console.log(`📡 Refreshing users page ${page}...`);
      const fetchedUsers = await fetchUsersFromBackend(page, pageSize);

      if (!fetchedUsers || fetchedUsers.length === 0) {
        console.log("✅ No more users to fetch.");
        break;
      }

      allFetchedUsers = [...allFetchedUsers, ...fetchedUsers];

      // Progressive display: update table after each batch
      setOperators([...allFetchedUsers]);

      if (fetchedUsers.length < pageSize) {
        break;
      }

      page += 1;
    }

    setFilteredOperators(allFetchedUsers);
  } catch (error) {
    console.error("Failed to refresh users:", error);
    setError(error.message || "Failed to refresh operators");
    setSnackbar({
      open: true,
      message: "Failed to refresh operators",
      severity: "error",
    });
  } finally {
    setLoading(false);
  }
};

  // Filter operators based on search query (name or email)
  React.useEffect(() => {
    let filtered = [...operators];
    // Search filter (operator name or email) - real-time
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (operator) =>
          operator.name?.toLowerCase().includes(query) ||
          operator.email?.toLowerCase().includes(query)
      );
    }

    // Sort filtered results with Admin always on top
    filtered.sort((a, b) => {
      if (a.role === "Admin" && b.role !== "Admin") return -1;
      if (a.role !== "Admin" && b.role === "Admin") return 1;
      return 0;
    });

    setFilteredOperators(filtered);
  }, [operators, searchQuery]);

  // Clear search filter on any page change
  React.useEffect(() => {
    // If page changed from the previous page, clear search filter
    if (previousPageRef.current !== paginationModel.page) {
      setSearchQuery("");
      // Update the previous page reference
      previousPageRef.current = paginationModel.page;
    }
  }, [paginationModel.page]);

  const handleMenuClick = (event, operator) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedOperator(operator);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    // Only clear selectedOperator if no dialogs are open
    if (!deleteDialogOpen) {
      setSelectedOperator(null);
    }
  };

  const handleAddUser = () => {
    setAddUserDialogOpen(true);
  };

  const handleDeleteUser = () => {
    setDeleteDialogOpen(true);
    setAnchorEl(null); // Close menu without clearing selectedOperator
  };

  const confirmAddUser = async () => {
    if (operationLoading) return; // Prevent multiple clicks

    const { name, email, password, role = "Operator" } = formData;

    try {
      setOperationLoading(true);

      await sendUserDataToBackend({
        email,
        name,
        role: role.toLowerCase(),
        password,
      });

      // Refresh the operators list after successful addition
      await refreshOperators();

      setSnackbar({
        open: true,
        message: `User ${name} registered successfully`,
        severity: "success",
      });
      setAddUserDialogOpen(false);
      setFormData({ name: "", email: "", password: "", role: "Operator" });
    } catch (error) {
      console.error("Registration error:", error);
      setSnackbar({
        open: true,
        message: error.message || "Failed to register user.",
        severity: "error",
      });
    } finally {
      setOperationLoading(false);
    }
  };

  const confirmDeleteUser = async () => {
    if (operationLoading) return;

    setOperationLoading(true);
    try {
      await deleteUserFromBackend(selectedOperator.id);

      // Refresh the operators list after successful deletion
      await refreshOperators();

      setSnackbar({
        open: true,
        message: `User ${selectedOperator.email} deleted successfully.`,
        severity: "success",
      });
      setDeleteDialogOpen(false);
      setSelectedOperator(null);
    } catch (error) {
      console.error("Error deleting user:", error);
      setSnackbar({
        open: true,
        message: error.message || "Failed to delete user.",
        severity: "error",
      });
    } finally {
      setOperationLoading(false);
    }
  };

  const cancelDeleteUser = () => {
    if (operationLoading) return; // Prevent closing during loading
    setDeleteDialogOpen(false);
    setSelectedOperator(null);
  };

  const handleFormChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const generatePassword = () => {
    const chars =
      "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    let password = "";
    for (let i = 0; i < 8; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setFormData((prev) => ({ ...prev, password }));
  };

  const copyToClipboard = () => {
    const textToCopy = `Name: ${formData.name}\nEmail: ${formData.email}\nPassword: ${formData.password}\nRole: ${formData.role}`;
    navigator.clipboard.writeText(textToCopy).then(() => {
      console.log("User data copied to clipboard");
    });
  };

  const cancelAddUser = () => {
    setAddUserDialogOpen(false);
    setFormData({ name: "", email: "", password: "", role: "Operator" });
  };

  // Columns definition
  const columns = [
    {
      field: "name",
      headerName: "Name",
      minWidth: 200,
      flex: 2,
    },
    {
      field: "email",
      headerName: "Email",
      minWidth: 250,
      flex: 3,
    },
    {
      field: "role",
      headerName: "Role",
      minWidth: 250,
      flex: 3,
      sortComparator: (v1, v2) => {
        // Admin should always come first
        if (v1 === "Admin" && v2 !== "Admin") return -1;
        if (v1 !== "Admin" && v2 === "Admin") return 1;
        // For other roles, use normal string comparison
        return v1.localeCompare(v2);
      },
    },
    {
      field: "actions",
      headerName: "",
      sortable: false,
      filterable: false,
      align: "center",
      headerAlign: "center",
      minWidth: 80,
      flex: 1,
      renderCell: (params) => (
        <IconButton
          onClick={(event) => handleMenuClick(event, params.row)}
          size="small"
        >
          <MoreVertIcon />
        </IconButton>
      ),
    },
  ];

  return (
    <Box sx={{ width: "100%", display: "flex", flexDirection: "column" }}>
      {/* Top Row */}
      {!embedded && (
        <Box sx={{ px: 1, py: 1, display: "flex", alignItems: "center", gap: 1.5 }}>
          <PersonIcon sx={{ fontSize: "1.5rem", color: "#333" }} />
          <Typography variant="h4" sx={{ fontWeight: "bold" }}>
            Operators
          </Typography>
        </Box>
      )}

      {embedded && (
        <Typography sx={{ px: 1, pt: 0.5, pb: 1.5, fontSize: "1.125rem", fontWeight: 600, color: "#111827" }}>
          Operators
        </Typography>
      )}

      {/* Search + actions row */}
      <Box
        sx={{
          display: "flex",
          gap: 1.5,
          alignItems: "center",
          flexWrap: "nowrap",
          mx: embedded ? 0 : 1,
          mb: 2,
          p: 1.5,
          backgroundColor: "#FFFFFF",
          border: "1px solid #e5e7eb",
          borderRadius: 2,
          boxShadow: "0 1px 2px rgba(16,24,40,0.04)",
          overflowX: "auto",
          scrollbarWidth: "none",
          msOverflowStyle: "none",
          "&::-webkit-scrollbar": { display: "none" },
        }}
      >
        <Box sx={{ flex: 1, minWidth: embedded ? 200 : 300 }}>
          <PillSearchField
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder="Enter name or email"
            resultCount={filteredOperators.length}
            loading={loading}
          />
        </Box>
        <Button
          variant="outlined"
          onClick={handleAddUser}
          disabled={loading}
          sx={{ textTransform: "none", borderRadius: 1.5, borderColor: "#163666", color: "#163666", px: 1.25, ml: "auto", flexShrink: 0, whiteSpace: "nowrap", "&:hover": { borderColor: "#112B51", backgroundColor: "rgba(22,54,102,0.06)" } }}
        >
          Add User
        </Button>
      </Box>

      {/* Data Table */}
      <Box sx={{ mx: embedded ? 0 : 1, mb: 2, backgroundColor: "#FFFFFF", border: "1px solid #e5e7eb", borderRadius: 2, overflow: "hidden" }}>
        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: 400,
            }}
          >
            <CircularProgress />
          </Box>
        ) : error ? (
          <Box
            sx={{
              p: 3,
              textAlign: "center",
              minHeight: 400,
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Typography color="error" variant="h6" sx={{ mb: 2 }}>
              Error loading operators
            </Typography>
            <Typography color="error" sx={{ mb: 3 }}>
              {error}
            </Typography>
            <Button
              variant="contained"
              color="primary"
              onClick={() => window.location.reload()}
            >
              Retry
            </Button>
          </Box>
        ) : (
          <DataGrid
            rows={filteredOperators}
            getRowId={(row) => row.id}
            columns={columns}
            autoHeight
            rowHeight={40}
            columnHeaderHeight={40}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[30, 50, 100]}
            disableRowSelectionOnClick
            disableColumnSelector
            disableDensitySelector
            sortingOrder={["asc", "desc"]}
            initialState={{
              sorting: {
                sortModel: [{ field: "role", sort: "asc" }],
              },
            }}
            getRowClassName={(params) => {
              if (params.row.role === "Admin") {
                if (
                  ["pn@sibolab.de"].includes(
                    params.row.email
                  )
                ) {
                  return "special-admin-row"; // greenish admins
                }
                return "admin-row"; // normal blue admins
              }
              return "";
            }}
            sx={{
              border: "none",
              borderRadius: 0,
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor: "#f5f5f5",
              },
              "& .admin-row": {
                backgroundColor: "rgba(25, 118, 210, 0.08)",
                "&:hover": {
                  backgroundColor: "rgba(25, 118, 210, 0.12)",
                },
              },
              "& .special-admin-row": {
                backgroundColor: "rgba(56, 142, 60, 0.08)", // greenish
                "&:hover": {
                  backgroundColor: "rgba(56, 142, 60, 0.12)",
                },
              },
            }}
          />
        )}
      </Box>

      {/* Context Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        {/* Reset-password action removed: no backend reset endpoint yet — see TODO in authService.js */}
        <MenuItem
          onClick={handleDeleteUser}
          sx={{ color: "error.main" }}
          disabled={
            selectedOperator?.role === "Admin" &&
            (selectedOperator?.email === "pn@sibolab.de")
          }
        >
          Delete User
        </MenuItem>
      </Menu>

      {/* Delete User Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={operationLoading ? undefined : cancelDeleteUser}
        aria-labelledby="delete-user-dialog-title"
        aria-describedby="delete-user-dialog-description"
      >
        <DialogTitle id="delete-user-dialog-title">Delete User</DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-user-dialog-description">
            Are you sure you want to permanently delete{" "}
            <strong>{selectedOperator?.name}</strong>? This action cannot be
            undone and will remove all associated data.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 2, gap: 1 }}>
          <Button
            onClick={cancelDeleteUser}
            variant="outlined"
            color="primary"
            disabled={operationLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmDeleteUser}
            color="error"
            variant="contained"
            autoFocus
            disabled={operationLoading}
          >
            {operationLoading ? "Deleting..." : "Delete User"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add User Dialog */}
      <Dialog
        open={addUserDialogOpen}
        onClose={operationLoading ? undefined : cancelAddUser}
        aria-labelledby="add-user-dialog-title"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle id="add-user-dialog-title">Add New User</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Name
            </Typography>
            <TextField
              fullWidth
              size="small"
              placeholder="Enter full name"
              variant="outlined"
              value={formData.name}
              onChange={(e) => handleFormChange("name", e.target.value)}
              sx={{ mb: 2 }}
              disabled={operationLoading}
            />

            <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Email
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Enter email address"
                  variant="outlined"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleFormChange("email", e.target.value)}
                  disabled={operationLoading}
                />
              </Box>
              <Box sx={{ width: 150 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Role
                </Typography>
                <FormControl fullWidth size="small">
                  <Select
                    value={formData.role}
                    onChange={(e) => handleFormChange("role", e.target.value)}
                    disabled={operationLoading}
                  >
                    <MenuItem value="Operator">Operator</MenuItem>
                    <MenuItem value="Admin">Admin</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </Box>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 1,
              }}
            >
              <Typography variant="body2">Password</Typography>
              <Button
                size="small"
                onClick={generatePassword}
                sx={{ fontSize: "0.75rem" }}
                disabled={operationLoading}
              >
                Generate
              </Button>
            </Box>
            <TextField
              fullWidth
              size="small"
              placeholder="Enter password"
              variant="outlined"
              type="text"
              value={formData.password}
              onChange={(e) => handleFormChange("password", e.target.value)}
              sx={{ mb: 2 }}
              disabled={operationLoading}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 2 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              width: "100%",
            }}
          >
            {/* Copy Button on the left */}
            <Box>
              {formData.name &&
                formData.email &&
                formData.password &&
                formData.role && (
                  <Chip
                    icon={<ContentCopyIcon sx={{ color: "#1976d2" }} />}
                    label="Copy User Details"
                    onClick={copyToClipboard}
                    disabled={operationLoading}
                    sx={{
                      bgcolor: "#e3f2fd",
                      color: "#1976d2",
                      borderRadius: 4,
                      px: 2,
                      py: 0.5,
                      fontSize: "0.875rem",
                      "&:hover": {
                        bgcolor: "#bbdefb",
                      },
                    }}
                  />
                )}
            </Box>

            {/* Action buttons on the right */}
            <Box sx={{ display: "flex", gap: 1.5 }}>
              <Button
                onClick={cancelAddUser}
                variant="outlined"
                color="primary"
                disabled={operationLoading}
              >
                Cancel
              </Button>
              <Button
                onClick={confirmAddUser}
                variant="contained"
                autoFocus
                disabled={
                  !formData.name ||
                  !formData.email ||
                  !formData.password ||
                  !formData.role ||
                  operationLoading
                }
                sx={{ bgcolor: "#163666", "&:hover": { bgcolor: "#112B51" } }}
              >
                {operationLoading ? "Adding User..." : "Add User"}
              </Button>
            </Box>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={snackbar.severity}
          variant="filled"
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default Operators;
